import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DateInput } from 'semantic-ui-calendar-react'
import { Button, Dimmer, Dropdown, Header, Icon, Input, Loader, Popup, Segment, Table } from 'semantic-ui-react'
import cloneDeep from 'lodash.clonedeep'
import notify from '~/components/Notify'
import { useAuth } from '~/routes/components/AuthProvider'
import styled from 'styled-components'
import { VatStyledWrapper } from '../../style'
import { FarmersQuery } from '~/QueryHooks/Farmers'
import { useParams } from 'react-router-dom'
import { FarmersService } from '~/api/services/farmers/index'
import { LIGHT_USER_ROLE_SLUG } from '~/Constants'
import { StyledNoResultsMessage } from '~/components/TableComponent/style'
import CustomIcon from '~/components/CustomIcon'
import { handleErrors } from '~/utils/helpers'

interface VatTableProps {
  createdAt: string
  farmerData: any
}

const VatTableWrapper = styled.div`
 .field {
  margin: 0 !important;
  opacity: 1 !important;
 }
 .header {
  margin-top: 0em !important;
  padding: 0rem !important;
  font-size: 14px !important;
  background: white !important;
 }
 .ui.fixed.table td {
  overflow: visible !important;
 }
 .segment {
  background: white !important;
 }
 .select-field {
 }
`

const VatTable: React.FC<VatTableProps> = (props) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { systemData: { languages, logged: { languageId, roleSlug } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)
  const [vatList, setVatList] = useState([])
  const [isBusy, setIsBusy] = useState(false)
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG

  const {
    data: vatOptions,
    isLoading: isBusyOptions
  } = FarmersQuery.getTaxOptions(id)

  useEffect(() => {
    fetchVatList()
  }, [])

  async function fetchVatList() {
    setIsBusy(true)
    try {
      const response = await FarmersService.getTaxation(id)
      const { data } = response
      data.saleTaxes.forEach((_, index) => {
        if (data.saleTaxes[index].dateFrom) {
          data.saleTaxes[index].dateFrom = dayjs(data.saleTaxes[index].dateFrom).format('DD.MM.YYYY')
        }
        if (data.saleTaxes[index].dateTo) {
          data.saleTaxes[index].dateTo = dayjs(data.saleTaxes[index].dateTo).format('DD.MM.YYYY')
        }
      })
      setVatList(data?.saleTaxes)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function parseDate(value) {
    const arrayOfValues = value.split('.')
    return `${arrayOfValues[2]}-${arrayOfValues[1]}-${arrayOfValues[0]}`
  }

  const handleKeyPress = (event) => {
    event.preventDefault();
  }

  function getDayBehind(date) {
    const inputDate = parseDate(date)
    const parsedDate = dayjs(inputDate, { format: 'DD.MM.YYYY' });
    if (parsedDate.isValid()) {
      const previousDay = parsedDate.subtract(1, 'day');
      return previousDay.format('DD.MM.YYYY');
    }
    return parsedDate.format('DD.MM.YYYY')
  }

  function getDayAhead(date) {
    const inputDate = parseDate(date)
    const parsedDate = dayjs(inputDate, { format: 'DD.MM.YYYY' });
    if (parsedDate.isValid()) {
      const previousDay = parsedDate.add(1, 'day');
      return previousDay.format('DD.MM.YYYY');
    }
    return parsedDate.format('DD.MM.YYYY')
  }

  async function handleUpdate() {
    try {
      if (vatList.filter(vat => vat.saleTaxId === '').length > 0) {
        notify(t('Please select tax'), 'error')
        return
      }
      setIsBusy(true)
      const tempVatList = cloneDeep(vatList)
      tempVatList.forEach((_, index) => {
        if (tempVatList[index].dateFrom) {
          tempVatList[index].dateFrom = parseDate(tempVatList[index].dateFrom)
        }
        if (tempVatList[index].dateTo) {
          tempVatList[index].dateTo = parseDate(tempVatList[index].dateTo)
        }
      })
      await FarmersService.updateTaxation(id, { saleTaxes: tempVatList })
      const companyData = `${props.farmerData.supplierNo} - ${props.farmerData.companyName}`
      notify(t('The VAT rate has been successfully updated for the farmer {{companyData}}.', { companyData }), 'success')
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderHeader() {
    return <VatStyledWrapper>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {vatList.length === 0 && <Button
          onClick={() => setVatList([...vatList, {
            saleTaxId: '',
            dateFrom: vatList.length === 0 ? null : getDayBehind(vatList[vatList.length - 1].dateTo),
            dateTo: null,
            id: null
          }])}
          icon
          style={{ marginTop: 5, minWidth: 215 }}
          labelPosition='left'
          primary
          type='button'
          size='small'
        >
          <Icon name='calculator' /> {t('Add VAT')}
        </Button>
        }
        {!lightUserLogged && <Button
          disabled={loading}
          onClick={() => handleUpdate()}
          style={{ color: 'white' }}
          type='button'
        >{t('Update')}
        </Button>}
      </div>
    </VatStyledWrapper>
  }

  function renderLoader() {
    return <Dimmer inverted active={loading}>
      <Loader active={loading} />
    </Dimmer>
  }

  function renderNoResultsMessage() {
    return <StyledNoResultsMessage className='no-result-message'>
      <Segment placeholder>
        <Header icon>
          <Icon name='search' />
          {t('No results found....')}
        </Header>
        <Segment.Inline>
        </Segment.Inline>
      </Segment>
    </StyledNoResultsMessage>
  }

  function handleDeleteFirstElement() {
    const tempArray = cloneDeep(vatList)
    if (tempArray.length > 1) {
      tempArray[1].dateFrom = tempArray[0].dateFrom
    }
    tempArray.splice(0, 1)
    setVatList(tempArray)
  }

  function HandleDeleteVatElement(index) {
    const tempArray = cloneDeep(vatList)
    tempArray[index + 1].dateFrom = getDayAhead(tempArray[index - 1].dateTo)
    tempArray.splice(index, 1)
    setVatList(tempArray)
  }

  function handleDeleteLastElement(index) {
    const tempArray = cloneDeep(vatList)
    tempArray[index - 1].dateTo = ''
    tempArray.splice(index, 1)
    setVatList(tempArray)
  }

  function handleDeleteVat(index) {
    switch (index) {
      case 0:
        return handleDeleteFirstElement()
      case vatList.length - 1:
        return handleDeleteLastElement(index)
      default:
        return HandleDeleteVatElement(index)
    }
  }

  function renderTableBody() {
    return <>
      {vatList?.map((vatTax, index) => {
        const vatTaxOptions = vatTax?.id ?
          vatTax?.list?.map(vatOption => ({ text: vatOption.saleTaxLabel, value: vatOption.id })) :
          vatOptions?.saleTaxes?.map(vatOption => ({ text: vatOption.saleTaxLabel, value: vatOption.id }))
        const removeDateFromField = index === 0 && !(typeof (vatTax?.dateFrom) === 'string')
        return <Table.Row key={vatTax.id ?? `vat-${Math.random()}`}>
          <Table.Cell textAlign='center'>
            <DateInput
              name='dateTime'
              onKeyPress={handleKeyPress}
              placeholder={t('Valid from')}
              disabled={true}
              clearable
              closable
              maxDate={vatTax.dateTo ? getDayBehind(vatTax.dateTo) : ''}
              initialDate={vatTax.dateTo ? getDayBehind(vatTax.dateTo) : ''}
              autoComplete='off'
              markColor={'blue'}
              localization={languageLocal.code}
              closeOnMouseLeave={false}
              dateFormat={'DD.MM.YYYY'}
              className={'datePicker'}
              value={removeDateFromField ? dayjs(props.createdAt).format('DD.MM.YYYY') : vatTax.dateFrom}
              iconPosition='left'
              onChange={(event, { name, value }) => {
                const tempVatList = cloneDeep(vatList)
                tempVatList[index].dateFrom = value
                setVatList(tempVatList)
              }}
            />
          </Table.Cell>
          <Table.Cell>
            <DateInput
              name='dateTime'
              onKeyPress={handleKeyPress}
              placeholder={t('Open')}
              clearable
              closable
              minDate={vatTax.dateFrom ? getDayAhead(vatTax.dateFrom) : ''}
              disabled={index !== vatList.length - 1 || lightUserLogged}
              initialDate={vatTax.dateFrom ? getDayAhead(vatTax.dateFrom) : ''}
              autoComplete='off'
              markColor={'blue'}
              localization={languageLocal.code}
              closeOnMouseLeave={false}
              dateFormat={'DD.MM.YYYY'}
              className={'datePicker'}
              value={vatTax.dateTo || ''}
              iconPosition='left'
              onChange={(event, { name, value }) => {
                const tempVatList = cloneDeep(vatList)
                tempVatList[index].dateTo = value
                const newDateFrom = value ? tempVatList[index].dateTo : tempVatList[index].dateFrom
                setVatList([...tempVatList, {
                  saleTaxId: '',
                  dateFrom: getDayAhead(newDateFrom),
                  dateTo: null,
                  id: null
                }])
              }}
            />
          </Table.Cell>
          <Table.Cell>
            <Dropdown
              selection
              disabled={lightUserLogged}
              placeholder={t('Vat')}
              upward
              selectOnBlur={false}
              scrolling
              value={vatTax.saleTaxId}
              options={vatTaxOptions}
              onChange={(e, data) => {
                const tempVatList = cloneDeep(vatList)
                tempVatList[index].saleTaxId = data.value
                setVatList(tempVatList)
              }
              }
            />
          </Table.Cell>
          {!lightUserLogged && <Table.Cell>
            <Popup
              content={t('Delete')}
              trigger={<CustomIcon
                color='red'
                name='trash'
                onClick={() => handleDeleteVat(index)}
                style={{ fontSize: '1.2em' }}
              />}
            />
          </Table.Cell>}
        </Table.Row>
      })}
    </>
  }

  function renderTable() {
    return <Table celled fixed singleLine>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>{t('Validity period from')}</Table.HeaderCell>
          <Table.HeaderCell>{t('Validity period to')}</Table.HeaderCell>
          <Table.HeaderCell>{t('VAT rate')}</Table.HeaderCell>
          {!lightUserLogged && <Table.HeaderCell width={'2'}>{t('Actions')}</Table.HeaderCell>}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {renderTableBody()}
      </Table.Body>
    </Table >
  }

  const loading = isBusy || isBusyOptions

  return <VatTableWrapper>
    {renderLoader()}
    {renderHeader()}
    <div>
      {renderTable()}
      {vatList.length === 0 && !isBusy && renderNoResultsMessage()}
    </div>
  </VatTableWrapper>
}
export default VatTable